import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { LoginRequest } from '@/api/generated/models';
import {
  useChangePassword,
  useLogin,
  useLogout
} from '@/api/generated/authentication/authentication';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { queryClient } from '@/store/queryClient';

export const useAuth = () => {
  const { user, isAuthenticated, login, logout, completePasswordChange } = useAuthContext();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  // "Zapamti me" se ne šalje backendu (čisto klijentska odluka gdje se token
  // sprema) — mutate-time vrijednost mora preživjeti do onSuccess, otud ref.
  const rememberRef = useRef(true);

  const rawLoginMutation = useLogin({
    mutation: {
      onSuccess: (data) => {
        if (data.accessToken && data.refreshToken && data.user) {
          // Query keys nisu scope-ani po korisniku/organizaciji — bez ovoga bi
          // prijava kao druga organizacija (bez punog reloada stranice) i dalje
          // prikazivala keširane podatke PRETHODNE organizacije dok staleTime
          // (5 min) ne istekne.
          queryClient.clear();
          login(
            data.accessToken,
            data.refreshToken,
            {
              id: data.user.id ?? '',
              email: data.user.email ?? '',
              firstName: data.user.firstName ?? '',
              lastName: data.user.lastName ?? '',
              role: data.user.role ?? '',
              orgName: data.user.orgName ?? '',
              organisationId: data.user.organisationId ?? '',
              mustChangePassword: data.user.mustChangePassword ?? false
            },
            rememberRef.current
          );
          navigate('/');
        }
      }
    }
  });

  // Adapter: LoginForm šalje { email, password, rememberMe } direktno
  // useLogin očekuje { data: LoginRequest } — dodajemo locale, izdvajamo rememberMe
  const loginMutation = {
    ...rawLoginMutation,
    mutate: (credentials: Omit<LoginRequest, 'locale'> & { rememberMe: boolean }) => {
      const { rememberMe, ...loginRequest } = credentials;
      rememberRef.current = rememberMe;
      rawLoginMutation.mutate({ data: { ...loginRequest, locale: i18n.language } });
    }
  };

  const logoutMutation = useLogout({
    mutation: {
      // onSettled garantira cleanup čak i ako backend logout zakaže
      onSettled: () => {
        queryClient.clear();
        logout();
        navigate('/login');
      }
    }
  });

  const handleLogout = () => {
    logoutMutation.mutate({ data: { locale: i18n.language } });
  };

  // Uspješna promjena lozinke izdaje svjež par tokena (stari access token nosi
  // zastarjeli mustChangePassword=true claim sve do sljedeće prijave/osvježenja).
  const changePasswordMutation = useChangePassword({
    mutation: {
      onSuccess: (data) => {
        if (data.accessToken && data.refreshToken) {
          completePasswordChange(data.accessToken, data.refreshToken);
          navigate('/');
        }
      }
    }
  });

  const changePassword = (currentPassword: string, newPassword: string) => {
    changePasswordMutation.mutate({ data: { currentPassword, newPassword } });
  };

  return {
    user,
    isAuthenticated,
    loginMutation,
    logout: handleLogout,
    changePasswordMutation,
    changePassword
  };
};
